/**
 * Bảng rank Liên Quân + tiện ích hiển thị (bản ESM).
 * Đồng bộ với api-cf/utils/rank.js — sửa một bên nhớ sửa bên còn lại.
 */

export const RANK_TIERS = [
  { key: 'dong', label: 'Đồng', divisions: 3, color: '#c07a4a', note: 'Không bị trừ sao khi thua' },
  { key: 'bac', label: 'Bạc', divisions: 3, color: '#9fb0c4' },
  { key: 'vang', label: 'Vàng', divisions: 4, color: '#e8b230' },
  { key: 'bach_kim', label: 'Bạch Kim', divisions: 5, color: '#35c4d0' },
  { key: 'kim_cuong', label: 'Kim Cương', divisions: 5, color: '#5b8dff', note: 'Bắt đầu có cấm/chọn tướng' },
  { key: 'tinh_anh', label: 'Tinh Anh', divisions: 5, color: '#a366f5' },
  { key: 'cao_thu', label: 'Cao Thủ', starMin: 0, starMax: 9, color: '#ff8a3d' },
  { key: 'dai_cao_thu', label: 'Đại Cao Thủ', starMin: 10, starMax: 49, color: '#ff6b4a' },
  { key: 'chien_tuong', label: 'Chiến Tướng', starMin: 50, starMax: 74, color: '#f0446e' },
  { key: 'chien_than', label: 'Chiến Thần', starMin: 75, starMax: 99, color: '#e02a8f' },
  { key: 'thach_dau', label: 'Huyền Thoại / Thách Đấu', starMin: 100, starMax: null, color: '#ffd028' },
];

export const RANK_TIER_KEYS = RANK_TIERS.map((t) => t.key);
export const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V'];

const STAR_ORDER_BASE = 600;
const STAR_ORDER_MAX_STARS = 200;
export const RANK_ORDER_MAX = 1000;

export const getTier = (key) => RANK_TIERS.find((t) => t.key === key) || null;

export const isStarTier = (key) => {
  const tier = getTier(key);
  return !!tier && tier.starMin !== undefined;
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** Suy tier từ số sao — nguồn sự thật duy nhất cho nhóm Cao Thủ trở lên. */
export function tierFromStars(stars) {
  const s = Math.max(0, Math.floor(Number(stars) || 0));
  const tier = RANK_TIERS.find(
    (t) => t.starMin !== undefined && s >= t.starMin && (t.starMax === null || s <= t.starMax)
  );
  return tier ? tier.key : 'cao_thu';
}

/** Thang 0..1000 để vẽ thanh tiến độ (cùng công thức với API). */
export function rankOrderOf(rank) {
  if (!rank || !rank.tier) return 0;
  const tier = getTier(rank.tier);
  if (!tier) return 0;

  const index = RANK_TIER_KEYS.indexOf(tier.key);
  if (tier.divisions) {
    const division = clamp(Math.floor(Number(rank.division) || tier.divisions), 1, tier.divisions);
    return Math.round(index * 100 + ((tier.divisions - division) / tier.divisions) * 100);
  }

  const stars = clamp(Math.floor(Number(rank.stars) || 0), 0, STAR_ORDER_MAX_STARS);
  return Math.round(STAR_ORDER_BASE + (stars / STAR_ORDER_MAX_STARS) * (RANK_ORDER_MAX - STAR_ORDER_BASE));
}

/** Phần trăm tiến độ trên toàn thang rank (dùng cho progress bar). */
export const rankPercentOf = (rank) => Math.round((rankOrderOf(rank) / RANK_ORDER_MAX) * 100);

/** Nhãn đầy đủ: "Kim Cương III" / "Chiến Tướng · 62 sao". */
export function rankLabelOf(rank) {
  if (!rank || !rank.tier) return '';
  const tier = getTier(rank.tier);
  if (!tier) return '';
  if (tier.divisions) {
    const division = clamp(Math.floor(Number(rank.division) || tier.divisions), 1, tier.divisions);
    return `${tier.label} ${ROMAN[division]}`;
  }
  return `${tier.label} · ${Math.max(0, Math.floor(Number(rank.stars) || 0))} sao`;
}

/** Nhãn ngắn cho badge trên card: "Kim Cương III" / "Chiến Tướng". */
export function rankShortLabelOf(rank) {
  if (!rank || !rank.tier) return '';
  const tier = getTier(rank.tier);
  if (!tier) return '';
  if (tier.divisions) {
    const division = clamp(Math.floor(Number(rank.division) || tier.divisions), 1, tier.divisions);
    return `${tier.label} ${ROMAN[division]}`;
  }
  return tier.label;
}

export const hasRank = (post) => !!(post && post.rank && post.rank.tier);

/** Bỏ dấu tiếng Việt — NFD không tách được đ/Đ nên phải thay tay. */
function stripDiacritics(str) {
  const COMBINING_MARKS = /[̀-ͯ]/g;
  return str
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}

/** Nhãn rank dạng phẳng — không dấu, số Ả Rập thay vì số La Mã, dùng để copy
 * text gửi khách (khác rankLabelOf: có dấu + số La Mã, dùng để hiển thị UI).
 * VD: Tinh Anh, đoàn I → "tinh anh 1". */
export function rankPlainLabelOf(rank) {
  if (!rank || !rank.tier) return '';
  const tier = getTier(rank.tier);
  if (!tier) return '';
  const slug = stripDiacritics(tier.label).toLowerCase();
  if (tier.divisions) {
    const division = clamp(Math.floor(Number(rank.division) || tier.divisions), 1, tier.divisions);
    return `${slug} ${division}`;
  }
  return `${slug} ${Math.max(0, Math.floor(Number(rank.stars) || 0))} sao`;
}

export const REPUTATION_LEVELS = [
  { key: 'low', label: 'Thấp', min: 0, max: 39, color: '#f0446e' },
  { key: 'fair', label: 'Trung bình', min: 40, max: 69, color: '#e8b230' },
  { key: 'good', label: 'Tốt', min: 70, max: 89, color: '#35c4d0' },
  { key: 'excellent', label: 'Rất tốt', min: 90, max: 100, color: '#3ddc84' },
];

export function normalizeReputation(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return clamp(Math.round(n), 0, 100);
}

export function reputationLevel(value) {
  const n = normalizeReputation(value);
  if (n === null) return null;
  return REPUTATION_LEVELS.find((l) => n >= l.min && n <= l.max) || REPUTATION_LEVELS[0];
}
